import { Table, Row, RowModel, RowData } from "@tanstack/table-core/src/types"
import { SortingFn } from "@tanstack/table-core/src/features/Sorting"

// NOTE: We cannot include [] as an "undefined value" becase
// the javascript evaluation of `[] === []` is false so we perform
// another comparison later to make up for it
const UNDEFINED_VALUES: unknown[] = [undefined, null, "", [], NaN]

export type NoInfer<T> = [T][T extends any ? 0 : never]

/**
 * Utility function from https://github.com/TanStack/table/blob/main/packages/table-core/src/utils.ts#L137
 * getSortedRowModel depends on this utility function
 */

export function memo<TDeps extends readonly any[], TResult>(
    getDeps: () => [...TDeps],
    fn: (...args: NoInfer<[...TDeps]>) => TResult,
    opts: {
        key: any
        debug?: () => any
        onChange?: (result: TResult) => void
    },
): () => TResult {
    let deps: any[] = []
    let result: TResult | undefined

    return () => {
        let depTime: number
        if (opts.key && opts.debug) depTime = Date.now()

        const newDeps = getDeps()

        const depsChanged =
            newDeps.length !== deps.length || newDeps.some((dep: any, index: number) => deps[index] !== dep)

        if (!depsChanged) {
            return result
        }

        deps = newDeps

        let resultTime: number
        if (opts.key && opts.debug) resultTime = Date.now()

        result = fn(...newDeps)
        opts?.onChange?.(result)

        if (opts.key && opts.debug) {
            if (opts?.debug()) {
                const depEndTime = Math.round((Date.now() - depTime) * 100) / 100
                const resultEndTime = Math.round((Date.now() - resultTime) * 100) / 100
                const resultFpsPercentage = resultEndTime / 16

                const pad = (str: number | string, num: number) => {
                    str = String(str)
                    while (str.length < num) {
                        str = " " + str
                    }
                    return str
                }

                console.info(
                    `%c⏱ ${pad(resultEndTime, 5)} /${pad(depEndTime, 5)} ms`,
                    `
              font-size: .6rem;
              font-weight: bold;
              color: hsl(${Math.max(0, Math.min(120 - 120 * resultFpsPercentage, 120))}deg 100% 31%);`,
                    opts?.key,
                )
            }
        }

        return result
    }
}

/**
 * Row model from
 * https://github.com/TanStack/table/blob/76a87cc061d4f72c40e7ee6b9a3f99a8f595f323/packages/table-core/src/utils/getSortedRowModel.ts
 * This version overrides the sortData function in the model to handle always sorting undefined values to the bottom
 */

export function getSortedRowModel<TData extends RowData>(): (table: Table<TData>) => () => RowModel<TData> {
    return (table) =>
        memo(
            () => [table.getState().sorting, table.getPreSortedRowModel()],
            (sorting, rowModel) => {
                if (!rowModel.rows.length || !sorting?.length) {
                    return rowModel
                }

                const sortingState = table.getState().sorting

                const sortedFlatRows: Row<TData>[] = []

                // Filter out sortings that correspond to non existing columns
                const availableSorting = sortingState.filter((sort) => table.getColumn(sort.id)?.getCanSort())

                const columnInfoById: Record<
                    string,
                    {
                        sortUndefined?: false | -1 | 1
                        invertSorting?: boolean
                        sortingFn: SortingFn<TData>
                    }
                > = {}

                availableSorting.forEach((sortEntry) => {
                    const column = table.getColumn(sortEntry.id)
                    if (!column) return

                    columnInfoById[sortEntry.id] = {
                        sortUndefined: column.columnDef.sortUndefined,
                        invertSorting: column.columnDef.invertSorting,
                        sortingFn: column.getSortingFn(),
                    }
                })

                const sortData = (rows: Row<TData>[]) => {
                    // This will also perform a stable sorting using the row index
                    // if needed.
                    const sortedData = rows.map((row) => ({ ...row }))

                    sortedData.sort((rowA, rowB) => {
                        for (let i = 0; i < availableSorting.length; i += 1) {
                            const sortEntry = availableSorting[i]
                            const columnInfo = columnInfoById[sortEntry.id]
                            const isDesc = sortEntry?.desc ?? false

                            let sortInt = 0

                            // All sorting ints should always return in ascending order
                            if (columnInfo.sortUndefined) {
                                const aValue = rowA.getValue(sortEntry.id)
                                const bValue = rowB.getValue(sortEntry.id)

                                // BEGIN Quorum Changes
                                /*
                                    cases for undefined values:
                                    1) Quorum-defined falsy values
                                    2) empty array, has to be checked as a length comparison
                                        due to note above
                                    3) column-specific falsy values
                                    4) Custom Field From Notes that always have an object
                                */
                                const valueIsUndefined = (value) =>
                                    UNDEFINED_VALUES.includes(value) ||
                                    value?.length === 0 ||
                                    (value?.value !== undefined && valueIsUndefined(value?.value))

                                const aUndefined = valueIsUndefined(aValue)
                                const bUndefined = valueIsUndefined(bValue)

                                if (aUndefined || bUndefined) {
                                    const undefinedSort =
                                        aUndefined && bUndefined
                                            ? 0
                                            : aUndefined
                                              ? columnInfo.sortUndefined
                                              : -columnInfo.sortUndefined

                                    // if (isDesc && undefinedSort !== 0) {
                                    //     undefinedSort *= -1
                                    // }

                                    return undefinedSort
                                }
                                // END Quorum Changes
                            }

                            if (sortInt === 0) {
                                sortInt = columnInfo.sortingFn(rowA, rowB, sortEntry.id)
                            }

                            // If sorting is non-zero, take care of desc and inversion
                            if (sortInt !== 0) {
                                if (isDesc) {
                                    sortInt *= -1
                                }

                                if (columnInfo.invertSorting) {
                                    sortInt *= -1
                                }

                                return sortInt
                            }
                        }

                        return rowA.index - rowB.index
                    })

                    // If there are sub-rows, sort them
                    sortedData.forEach((row) => {
                        sortedFlatRows.push(row)
                        if (row.subRows?.length) {
                            row.subRows = sortData(row.subRows)
                        }
                    })

                    return sortedData
                }

                return {
                    rows: sortData(rowModel.rows),
                    flatRows: sortedFlatRows,
                    rowsById: rowModel.rowsById,
                }
            },
            {
                key: process.env.NODE_ENV === "development" && "getSortedRowModel",
                debug: () => table.options.debugAll ?? table.options.debugTable,
                onChange: () => {
                    table._autoResetPageIndex()
                },
            },
        )
}
